import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { 
  Modal, Button, Form, Row, Col, Alert, Card, Badge,
  InputGroup, Spinner
} from 'react-bootstrap';
import { Plus, Trash2, Calculator, Check, X } from 'lucide-react';
import saleService from '../../services/saleService';

const MultiPaymentModal = ({ 
  show, 
  onHide, 
  saleTotal = 0, 
  onConfirm, 
  existingPayments = [] 
}) => {
  const [payments, setPayments] = useState([]);
  const [totalPaid, setTotalPaid] = useState(0);
  const [isValid, setIsValid] = useState(false);
  const [errors, setErrors] = useState([]);
  const [loading, setLoading] = useState(false);

  // ✅ SOLUTION 1 : Méthodes de paiement en constante (pas de recalcul)
  const availablePaymentMethods = useMemo(() => {
    return [
      { value: 'CASH', label: 'Espèces', icon: '💵' },
      { value: 'WAVE', label: 'Wave', icon: '📱' },
      { value: 'ORANGE_MONEY', label: 'Orange Money', icon: '🍊' },
      { value: 'CARD', label: 'Carte bancaire', icon: '💳' },
      { value: 'BANK_TRANSFER', label: 'Virement', icon: '🏦' },
      { value: 'CHECK', label: 'Chèque', icon: '🧾' },
    ];
  }, []);

  // ✅ SOLUTION 2 : formatCurrency stable
  const formatCurrency = useCallback((amount) => {
    if (typeof amount !== 'number' || isNaN(amount)) {
      return '0 FCFA';
    }
    return `${amount.toLocaleString('fr-FR')} FCFA`;
  }, []);

  // ✅ SOLUTION 3 : Convertir saleTotal une seule fois
  const validSaleTotal = useMemo(() => {
    const total = parseFloat(saleTotal);
    return isNaN(total) ? 0 : total;
  }, [saleTotal]);

  // ✅ SOLUTION 4 : Initialisation UNIQUEMENT au montage et quand show change
  useEffect(() => {
    if (!show) {
      // Reset quand le modal se ferme
      setPayments([]);
      setTotalPaid(0);
      setIsValid(false);
      setErrors([]);
      setLoading(false);
      return;
    }

    // Initialisation seulement quand le modal s'ouvre
    console.log('🔄 Initialisation MultiPaymentModal:', { validSaleTotal, existingPayments });
    
    if (existingPayments && existingPayments.length > 0) {
      const formattedPayments = existingPayments.map(p => ({
        method: p.method || 'CASH',
        amount: String(p.amount || ''),
        reference: p.reference || '',
        notes: p.notes || ''
      }));
      setPayments(formattedPayments);
    } else {
      setPayments([{
        method: 'CASH',
        amount: '',
        reference: '',
        notes: ''
      }]);
    }
  }, [show]); // ✅ UNIQUEMENT show comme dépendance

  // ✅ SOLUTION 5 : Validation et calcul dans un seul useEffect optimisé
  useEffect(() => {
  console.log('🔄 Validation triggered:', {
    paymentsCount: payments.length,
    validSaleTotal,
    paymentsData: payments
  });
  
  if (payments.length === 0) {
    setErrors(['Au moins un paiement est requis']);
    setTotalPaid(0);
    setIsValid(false);
    return;
  }

  let calculatedTotal = 0;
  const validationErrors = [];
  
  payments.forEach((payment, index) => {
    // Validation de la méthode
    if (!payment.method) {
      validationErrors.push(`Méthode de paiement manquante pour le paiement ${index + 1}`);
    }
    
    // Validation et calcul du montant
    const amount = parseFloat(payment.amount || 0);
    if (!payment.amount || amount <= 0 || isNaN(amount)) {
      validationErrors.push(`Montant invalide pour le paiement ${index + 1}: "${payment.amount}"`);
    } else {
      calculatedTotal += amount;
    }
  });
  
  console.log('💰 Calculs:', {
    calculatedTotal,
    validSaleTotal,
    difference: Math.abs(calculatedTotal - validSaleTotal),
    isValidTotal: validSaleTotal > 0
  });
  
  // ✅ Validation du total final - Seulement si saleTotal est valide
  if (validSaleTotal > 0 && Math.abs(calculatedTotal - validSaleTotal) > 0.01) {
    validationErrors.push(
      `Le total des paiements (${formatCurrency(calculatedTotal)}) doit correspondre au total de la vente (${formatCurrency(validSaleTotal)})`
    );
  }
  
  // Mise à jour des états
  setTotalPaid(calculatedTotal);
  setErrors(validationErrors);
  setIsValid(validationErrors.length === 0 && calculatedTotal > 0 && validSaleTotal > 0);
  
  console.log('✅ Validation result:', {
    totalPaid: calculatedTotal,
    errors: validationErrors,
    isValid: validationErrors.length === 0 && calculatedTotal > 0 && validSaleTotal > 0
  });
  
}, [payments, validSaleTotal, formatCurrency]);

  // ✅ SOLUTION 6 : Fonctions d'action stables avec useCallback
  const updatePayment = useCallback((index, field, value) => {
    setPayments(current => {
      const newPayments = [...current];
      if (newPayments[index]) {
        newPayments[index] = {
          ...newPayments[index],
          [field]: value
        };
      }
      return newPayments;
    });
  }, []);

  const addPayment = useCallback(() => {
    setPayments(current => [
      ...current,
      { method: 'CASH', amount: '', reference: '', notes: '' }
    ]);
  }, []);

  const removePayment = useCallback((index) => {
    setPayments(current => {
      if (current.length <= 1) return current;
      return current.filter((_, i) => i !== index);
    });
  }, []);

  const autoFillRemaining = useCallback(() => {
  console.log('🔧 Auto-fill remaining:', { validSaleTotal, payments });
  
  if (validSaleTotal <= 0) {
    console.log('❌ Total invalide pour auto-fill:', validSaleTotal);
    return;
  }
  
  setPayments(current => {
    if (current.length === 0) return current;
    
    const currentTotal = current.reduce((sum, p) => {
      const amount = parseFloat(p.amount || 0);
      return sum + (isNaN(amount) ? 0 : amount);
    }, 0);
    
    const remaining = validSaleTotal - currentTotal;
    
    console.log('📊 Auto-fill calc:', {
      currentTotal,
      remaining,
      validSaleTotal
    });
    
    if (remaining <= 0) return current;
    
    const newPayments = [...current];
    const lastIndex = newPayments.length - 1;
    const lastAmount = parseFloat(newPayments[lastIndex].amount || 0);
    
    newPayments[lastIndex] = {
      ...newPayments[lastIndex],
      amount: String(lastAmount + remaining)
    };
    
    console.log('✅ Auto-filled payment:', newPayments[lastIndex]);
    return newPayments;
  });
}, [validSaleTotal]);


  const handleConfirm = useCallback(async () => {
    if (!isValid || loading) return;

    try {
      setLoading(true);
      
      const formattedPayments = payments.map(payment => ({
        method: payment.method,
        amount: parseFloat(payment.amount),
        reference: payment.reference?.trim() || null,
        notes: payment.notes?.trim() || null
      }));
      
      console.log('✅ Confirmation paiements:', formattedPayments);
      await onConfirm(formattedPayments);
      
    } catch (error) {
      console.error('❌ Erreur confirmation:', error);
      setErrors([error.message || 'Erreur lors de la confirmation']);
    } finally {
      setLoading(false);
    }
  }, [isValid, loading, payments, onConfirm]);

  const handleClose = useCallback(() => {
    onHide();
  }, [onHide]);

  // ✅ Early return pour éviter le rendering inutile
  if (!show) return null;

  return (
    <Modal show={show} onHide={handleClose} size="lg" backdrop="static">
      <Modal.Header closeButton className="bg-info text-white">
        <Modal.Title>
          <Calculator size={20} className="me-2" />
          🔄 Paiement Combiné
        </Modal.Title>
      </Modal.Header>
      
      <Modal.Body>

        {/* ✅ Debug Info amélioré */}
      {/* {process.env.NODE_ENV === 'development' && (
        <Alert variant="secondary" className="small">
          <div>Debug Info:</div>
          <div>• saleTotal reçu: {validSaleTotal} FCFA</div>
          <div>• totalPaid calculé: {totalPaid} FCFA</div>
          <div>• Nombre paiements: {payments.length}</div>
          <div>• isValid: {isValid ? '✅' : '❌'}</div>
          <div>• Erreurs: {errors.length}</div>
        </Alert>
      )} */}

        {/* Debug Info - À SUPPRIMER en production */}
        {/* {process.env.NODE_ENV === 'development' && (
          <Alert variant="info" className="small">
            Debug: saleTotal={validSaleTotal}, totalPaid={totalPaid}, payments={payments.length}
          </Alert>
        )} */}

        {/* Alert Total */}
        {/* Alert Total avec plus d'infos */}
      <Alert variant="info" className="d-flex justify-content-between align-items-center">
        <div>
          <strong>Total à payer :</strong> {formatCurrency(validSaleTotal)} FCFA
          <br />
          <small className="text-muted">
            Payé: {formatCurrency(totalPaid)} FCFA
            {validSaleTotal > 0 && (
              <> • Reste: {formatCurrency(Math.max(0, validSaleTotal - totalPaid))} FCFA</>
            )}
          </small>
        </div>
        <Badge 
          bg={Math.abs(totalPaid - validSaleTotal) < 0.01 ? 'success' : 'warning'} 
          className="fs-6"
        >
          {Math.abs(totalPaid - validSaleTotal) < 0.01 ? '✅ OK' : '⚠️ Incomplet'}
        </Badge>
      </Alert>

      {/* Liste des erreurs */}
      {errors.length > 0 && (
        <Alert variant="danger">
          <div><strong>Erreurs à corriger :</strong></div>
          {errors.map((error, index) => (
            <div key={index}>• {error}</div>
          ))}
        </Alert>
      )}

        {/* Section Paiements */}
        <Card className="mb-3">
          <Card.Header className="d-flex justify-content-between align-items-center">
            <h6 className="mb-0">Répartition des Paiements</h6>
            <div>
              <Button 
                variant="outline-success" 
                size="sm" 
                onClick={addPayment}
                className="me-2"
                disabled={loading}
              >
                <Plus size={16} /> Ajouter
              </Button>
              <Button 
                variant="outline-info" 
                size="sm" 
                onClick={autoFillRemaining}
                disabled={validSaleTotal - totalPaid <= 0 || loading}
              >
                <Calculator size={16} /> Auto-compléter
              </Button>
            </div>
          </Card.Header>
          
          <Card.Body>
            {payments.map((payment, index) => (
              <Row key={index} className="mb-3 p-3 border rounded">
                <Col md={6}>
                  <Form.Label className="small text-muted">Méthode</Form.Label>
                  <Form.Select 
                    value={payment.method}
                    onChange={(e) => updatePayment(index, 'method', e.target.value)}
                    size="sm"
                    disabled={loading}
                  >
                    {availablePaymentMethods.map(method => (
                      <option key={method.value} value={method.value}>
                        {method.icon} {method.label}
                      </option>
                    ))}
                  </Form.Select>
                </Col>
                
                <Col md={6}>
                  <Form.Label className="small text-muted">Montant (FCFA)</Form.Label>
                  <InputGroup size="sm">
                    <Form.Control
                      type="number"
                      onWheel={e => e.target.blur()} // Empêche le scroll de changer la valeur
                      step="0.01"
                      min="0"
                      placeholder="0"
                      value={payment.amount}
                      onChange={(e) => updatePayment(index, 'amount', e.target.value)}
                      disabled={loading}
                    />
                    <InputGroup.Text>FCFA</InputGroup.Text>
                  </InputGroup>
                </Col>
                
                {/* <Col md={3}>
                  <Form.Label className="small text-muted">Référence</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="Référence..."
                    value={payment.reference}
                    onChange={(e) => updatePayment(index, 'reference', e.target.value)}
                    disabled={loading}
                  />
                </Col> */}
                
                {/* <Col md={2}>
                  <Form.Label className="small text-muted">Notes</Form.Label>
                  <Form.Control
                    type="text"
                    size="sm"
                    placeholder="Notes..."
                    value={payment.notes}
                    onChange={(e) => updatePayment(index, 'notes', e.target.value)}
                    disabled={loading}
                  />
                </Col> */}
                
                <Col md={1} className="d-flex align-items-end">
                  {payments.length > 1 && (
                    <Button 
                      variant="outline-danger" 
                      size="sm"
                      onClick={() => removePayment(index)}
                      disabled={loading}
                    >
                      <Trash2 size={14} />
                    </Button>
                  )}
                </Col>
              </Row>
            ))}
          </Card.Body>
        </Card>

        {/* Résumé */}
        <Card className="border-0 bg-light">
          <Card.Body>
            <Row>
              <Col md={6}>
                <div className="d-flex justify-content-between">
                  <span>Total saisi :</span>
                  <strong className={totalPaid === validSaleTotal ? 'text-success' : 'text-warning'}>
                    {formatCurrency(totalPaid)} FCFA
                  </strong>
                </div>
                <div className="d-flex justify-content-between">
                  <span>Reste à payer :</span>
                  <strong className={validSaleTotal - totalPaid === 0 ? 'text-success' : 'text-danger'}>
                    {formatCurrency(validSaleTotal - totalPaid)} FCFA
                  </strong>
                </div>
              </Col>
              <Col md={6}>
                <div className="d-flex justify-content-between">
                  <span>Nombre de paiements :</span>
                  <Badge bg="info">{payments.length}</Badge>
                </div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        {/* Erreurs */}
        {/* {errors.length > 0 && (
          <Alert variant="danger" className="mt-3">
            <strong>⚠️ Erreurs détectées :</strong>
            <ul className="mb-0 mt-2">
              {errors.map((error, index) => (
                <li key={index}>{error}</li>
              ))}
            </ul>
          </Alert>
        )} */}
      </Modal.Body>
      
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose} disabled={loading}>
          <X size={16} className="me-1" />
          Annuler
        </Button>
        <Button 
          variant="primary" 
          onClick={handleConfirm}
          disabled={!isValid || loading}
        >
          {loading ? (
            <>
              <Spinner size="sm" className="me-2" />
              Traitement...
            </>
          ) : (
            <>
              <Check size={16} className="me-1" />
              Confirmer les Paiements ({payments.length})
            </>
          )}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default MultiPaymentModal;